import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import type { MentionTarget } from "./mentions";

/**
 * The people you can @mention: members of the ACTIVE workspace only.
 *
 * `sales` is the global user-profile table (ADR-0006), not a tenant table, so
 * listing it directly offers colleagues from other tenants. The database
 * already refuses to notify them, the mention trigger drops any id that is
 * not a member, but surfacing the names at all would leak who else uses
 * Gravun. Found in browser QA: the picker showed "Example Owner" to an Acme
 * user. Joining through `workspace_members` is the fix.
 */
export const useTeammates = () => {
  const workspaceId = getActiveWorkspaceId();

  return useQuery<MentionTarget[]>({
    queryKey: ["gravun", "mentionable", workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const members = await supabase
        .from("workspace_members")
        .select("user_id")
        .eq("workspace_id", workspaceId!);
      if (members.error) throw members.error;

      const userIds = (members.data ?? []).map((row) => row.user_id as string);
      if (userIds.length === 0) return [];

      const profiles = await supabase
        .from("sales")
        .select("id, first_name, last_name")
        .in("user_id", userIds)
        .eq("disabled", false)
        .order("first_name");
      if (profiles.error) throw profiles.error;
      return (profiles.data ?? []) as MentionTarget[];
    },
  });
};
